// src/components/cards/MarketCapCard.tsx
import React from 'react';
import { MarketCapData } from '../../types';
import { LineChart } from '../charts';
import { subDays, format } from 'date-fns';
import DataStatusIndicator, { DataStatus } from '../DataStatusIndicator';

interface MarketCapCardProps {
  data: MarketCapData | null;
  loading: boolean;
  error: string | null;
  lastUpdated: number;
  formatTimestamp: (timestamp: number) => string;
  timeframe: string;
  dataStatus?: DataStatus;
}

const MarketCapCard: React.FC<MarketCapCardProps> = ({
  data,
  loading,
  error,
  lastUpdated,
  formatTimestamp,
  timeframe,
  dataStatus = 'live'
}) => {
  // Debugging: Log received data
  console.log('MarketCapCard received data:', data);
  if (data) {
    console.log('MarketCapCard historicalData:', data.historicalData);
  }

  // Format large numbers with commas and abbreviations
  const formatMarketCap = (value: number): string => {
    if (value >= 1_000_000_000_000) {
      return `$${(value / 1_000_000_000_000).toFixed(2)}T`;
    } else if (value >= 1_000_000_000) {
      return `$${(value / 1_000_000_000).toFixed(2)}B`;
    } else {
      return `$${(value / 1_000_000).toFixed(2)}M`;
    }
  };

  // Get color based on percentage to target
  const getProgressColor = (percent: number): string => {
    if (percent >= 90) return '#008000'; // Extreme Greed (green)
    if (percent >= 75) return '#90EE90'; // Greed (light green)
    if (percent >= 50) return '#FFD700'; // Neutral (yellow)
    if (percent >= 25) return '#FF6B6B'; // Fear (light red)
    return '#EA3943'; // Extreme Fear (red)
  };

  // Custom Tooltip for the chart
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      return (
        <div className="p-2 bg-gray-800 rounded-md shadow-lg text-white text-sm">
          <p className="font-bold mb-1">{format(new Date(label), 'MMM d, yyyy')}</p>
          <p className="text-blue-400">Market Cap: {formatMarketCap(payload[0].value)}</p>
        </div>
      );
    }
    return null;
  };

  // Prepare chart data, with fallback for missing/empty historicalData
  let chartData = data?.historicalData || [];
  if (data && (!data.historicalData || data.historicalData.length === 0)) {
    console.log('MarketCapCard: historicalData is missing or empty. Generating fallback chart data.');
    // Create minimal fallback data to ensure chart renders
    const now = data.timestamp || Date.now();
    chartData = [
      { timestamp: subDays(now, 7).getTime(), value: data.totalMarketCap * 0.98 }, // 7 days ago, 2% lower
      { timestamp: subDays(now, 1).getTime(), value: data.totalMarketCap * 0.99 }, // 1 day ago, 1% lower
      { timestamp: now, value: data.totalMarketCap }, // Current
    ];
  }


  return (
    <div className="bg-dashboard-card-light dark:bg-dashboard-card-dark rounded-card shadow-card p-5 dashboard-card relative">
      <DataStatusIndicator status={dataStatus} lastUpdated={lastUpdated} />
      
      <div className="flex justify-between items-start mb-4">
        <h3 className="text-lg font-semibold">Total Market Cap</h3>
        {loading && (
          <div className="animate-pulse px-2 py-1 bg-gray-200 dark:bg-gray-700 rounded text-xs">
            Updating...
          </div>
        )}
      </div>

      {error ? (
        <div className="text-red-500 py-8 text-center">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>{error}</p>
        </div>
      ) : data ? (
        <>
          {/* Market Cap Value */}
          <div className="text-3xl font-bold mb-2">
            {formatMarketCap(data.totalMarketCap)}
          </div>

          {/* Progress to 7T */}
          <div className="mb-4">
            <div className="flex justify-between text-sm mb-1">
              <span>Progress to $7T</span>
              <span className="font-medium">{data.percentToTarget.toFixed(1)}%</span>
            </div>
            <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
              <div 
                className="h-full rounded-full" 
                style={{
                  width: `${data.percentToTarget}%`,
                  backgroundColor: getProgressColor(data.percentToTarget)
                }}
              />
            </div>
          </div>

          {/* BTC and ETH Dominance */}
          <div className="grid grid-cols-2 gap-4 mb-4">
            <div className="bg-bitcoin-light dark:bg-bitcoin-dark/20 p-3 rounded-lg">
              <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
                BTC Dominance
              </div>
              <div className="text-lg font-medium text-bitcoin">
                {data.btcDominance.toFixed(1)}%
              </div>
            </div>
            <div className="bg-ethereum-light dark:bg-ethereum-dark/20 p-3 rounded-lg">
              <div className="text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark mb-1">
                ETH Dominance
              </div>
              <div className="text-lg font-medium text-ethereum">
                {data.ethDominance.toFixed(1)}%
              </div>
            </div>
          </div>

          {/* Market Cap Chart */}
          <div className="mb-3">
            <LineChart
              data={data.historicalData || []}
              color="#3b82f6"
              gradientColors={['rgba(59, 130, 246, 0.2)', 'rgba(59, 130, 246, 0)']}
              height={128}
              formatValue={(value) => formatMarketCap(value)}
              formatTimestamp={(timestamp) => new Date(timestamp).toLocaleDateString()}
              showArea={true}
              showPoints={false}
            />
          </div>

          {/* Last Updated */}
          <div className="mt-2 text-xs text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
            Last updated: {formatTimestamp(lastUpdated || data.timestamp)}
          </div>
        </>
      ) : (
        <div className="py-8 text-center text-dashboard-text-muted-light dark:text-dashboard-text-muted-dark">
          <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10 mx-auto mb-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p>No market cap data available</p>
        </div>
      )}
    </div>
  );
};

export default MarketCapCard;
